import { css } from "lit";

export const styles = css`
  :host {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--sds-color-background-default-default);
    --wow-input-padding: 10px 12px;
    --wow-input-padding-inline: 12px;
  }

  .page-container {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    width: 100%;
    box-sizing: border-box;
  }

  /* Page-level two-column layout (desktop) */
  .page-layout {
    display: grid;
    grid-template-columns: 1fr 2fr;
    flex: 1;
    overflow: hidden;
  }

  .page-left-column {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
    padding: 24px 24px 0;
    border-right: 1px solid var(--sds-color-border-default-default);
    box-sizing: border-box;
  }

  .page-left-scroll {
    flex: 1;
    overflow: hidden auto;
    min-height: 0;
    padding-bottom: 24px;
  }

  .page-right-column {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    min-height: 0;
  }

  /* Page Header */
  .page-header {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-600);
    padding-bottom: var(--sds-size-space-1200);
  }

  .page-title {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-06) var(--sds-typography-family-title);
    color: var(--sds-color-text-default-default);
    margin: 0;
    line-height: 20px;
  }

  .page-subtitle {
    font: var(--sds-typography-weight-regular) var(--sds-typography-body-size-medium) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-tertiary);
    margin: 0;
  }

  /* Form Panel */
  .form-panel {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    padding-top: 24px;
  }

  .section-divider {
    border: none;
    border-top: 1px solid var(--sds-color-border-default-default);
    margin: 0;
    padding: 0;
  }

  .form-panel > .section-divider {
    margin-left: -24px;
    margin-right: -24px;
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .form-row {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .form-row > * {
    flex: 1;
  }

  /* Toggle Field */
  .toggle-field {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .toggle-sub-fields {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    padding-top: var(--sds-size-space-400);
  }

  /* Right Column Content Panel */
  .content-panel {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-1600);
    padding: var(--sds-size-space-1200) var(--sds-size-space-2400);
  }

  .content-panel-header {
    display: flex;
    flex-direction: column;
  }

  .content-panel-label {
    font: var(--sds-typography-weight-medium) var(--sds-typography-scale-05) var(--sds-typography-family-title);
    color: var(--sds-color-text-default-default);
    margin: 0;
    padding: var(--sds-size-space-1200) var(--sds-size-space-2400) 0;
  }

  .content-panel-subtitle {
    color: var(--sds-color-text-default-secondary);
    font-size: var(--sds-typography-body-size-medium);
    line-height: 1.4;
    padding: var(--sds-size-space-400) var(--sds-size-space-2400) var(--sds-size-space-1200);
    border-bottom: 1px solid var(--sds-color-border-default-default);
  }

  /* Thumbnail upload area */
  .thumbnail-upload-container {
    display: flex;
    aspect-ratio: 16 / 9;
    border-radius: var(--sds-size-radius-400);
    overflow: hidden;
  }

  .thumbnail-upload-container media-uploader {
    width: 100%;
    height: 100%;
  }

  .thumbnail-error {
    font: var(--sds-typography-weight-regular) var(--sds-typography-body-size-small) var(--sds-typography-family-body);
    color: var(--sds-color-text-danger-secondary);
  }

  /* Character counter */
  .char-count {
    font: var(--sds-typography-weight-regular) var(--sds-typography-body-size-small) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-tertiary);
    text-align: right;
    margin-top: var(--sds-size-space-300);
  }

  /* Links tab placeholder */
  .links-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--sds-size-space-2400);
    font: var(--sds-typography-weight-regular) var(--sds-typography-body-size-medium) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-tertiary);
    text-align: center;
  }

  /* Footer */
  .page-footer {
    flex-shrink: 0;
    padding: 16px 24px;
    background: var(--sds-color-background-default-default);
    border-top: 1px solid var(--sds-color-border-default-default);
  }

  .footer-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .footer-left {
    display: flex;
    align-items: center;
  }

  .footer-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  /* Mobile tab content */
  .mobile-info-tab,
  .mobile-links-tab {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-800);
    padding-top: 24px;
  }

  /* Loading */
  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--sds-size-space-2400);
    font: var(--sds-typography-weight-regular) var(--sds-typography-body-size-medium) var(--sds-typography-family-body);
    color: var(--sds-color-text-default-tertiary);
  }

  @media (max-width: 1100px) {
    .page-layout {
      grid-template-columns: 1fr 1fr;
    }

    .content-panel-label {
      padding: var(--sds-size-space-1200) var(--sds-size-space-1200) 0;
    }

    .content-panel-subtitle {
      padding: var(--sds-size-space-400) var(--sds-size-space-1200) var(--sds-size-space-1200);
    }

    .content-panel {
      padding: var(--sds-size-space-1200);
    }
  }

  /* Responsive (mobile) */
  @media (max-width: 768px) {
    :host {
      overflow-y: auto;
    }

    .page-container {
      flex: none;
      overflow-y: visible;
      padding: var(--sds-size-space-800);
    }

    .page-header {
      padding-bottom: 0;
    }

    .mobile-title {
      font-size: 24px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
      padding: var(--sds-size-space-300) var(--sds-size-space-800) var(--sds-size-space-1200) var(--sds-size-space-800);
      margin-left: calc(-1 * var(--sds-size-space-800));
      margin-right: calc(-1 * var(--sds-size-space-800));
    }

    .page-footer {
      padding: 12px 16px;
    }

    .footer-actions {
      width: 100%;
    }

    .footer-actions wow-button {
      flex: 1;
    }
  }

  /* Custom input label color override */
  wow-input,
  wow-textarea,
  wow-select {
    --wow-input-label-color: var(--sds-color-text-default-tertiary);
  }

  wow-input,
  wow-select {
    --wow-input-placeholder-padding-top: 18px;
  }

  wow-select {
    --wow-input-label-white-space: normal;
  }

  wow-textarea::part(textarea) {
    min-height: 120px;
  }

  /* Links tab */
  .community-links-content {
    display: flex;
    flex-direction: column;
    gap: var(--sds-size-space-600);
    padding-top: 24px;
  }

  .community-links-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sds-size-space-400);
    flex-wrap: wrap;
  }

  .community-links-filters {
    display: flex;
    gap: var(--sds-size-space-200);
  }

  .community-links-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--sds-size-space-400);
    width: 100%;
  }

  .community-links-grid.drag-active {
    position: relative;
    user-select: none;
  }

  .community-links-grid.drag-active link-card.dragging {
    opacity: 0.5;
    transform: scale(0.95);
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .links-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: var(--sds-size-space-600);
    padding: var(--sds-size-space-2400);
    color: var(--sds-color-text-default-tertiary);
    font-size: var(--sds-typography-body-size-medium);
  }

  .links-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--sds-size-space-2400);
    color: var(--sds-color-text-default-tertiary);
    font-size: var(--sds-typography-body-size-medium);
  }
`;
